/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useCallback, useMemo } from 'react';
import { Typography } from '@altermaind/web/ui';
import { RowItemRenderValue, TableProps } from './types';

type Props = Pick<TableProps, 'columns' | 'defaultCellVariant' | 'rowWrapperProps'>;

export const useTable = ({ columns, defaultCellVariant, rowWrapperProps }: Props) => {
  const gridTemplateColumns = useMemo(
    () =>
      `${columns
        .map(col => {
          if (col.fitContent) {
            return 'auto';
          }
          if (!col.width) {
            return '1fr';
          }
          return typeof col.width === 'string' ? col.width : `${col.width}px`;
        })
        .join(' ')}`,
    [columns],
  );

  const renderRowCell = useCallback(
    (rowItem: any, renderValue: RowItemRenderValue): ReactNode => {
      if (!renderValue) {
        return null;
      }
      return typeof renderValue === 'string' ? (
        <Typography variant={defaultCellVariant}>{rowItem[renderValue]}</Typography>
      ) : (
        renderValue(rowItem)
      );
    },
    [defaultCellVariant],
  );

  const mergeStyle = useCallback((obj: any, styleToMerge: any) => {
    if (obj.style) {
      Object.assign(obj.style, styleToMerge);
    } else {
      Object.assign(obj, styleToMerge);
    }
    return obj;
  }, []);

  const rowProps = useCallback(
    (item: any, index: number) => {
      if (typeof rowWrapperProps === 'function') {
        return mergeStyle(rowWrapperProps(item, index), {
          gridTemplateColumns,
        });
      }
      return mergeStyle(rowWrapperProps, { gridTemplateColumns });
    },
    [gridTemplateColumns, mergeStyle, rowWrapperProps],
  );

  return { gridTemplateColumns, renderRowCell, rowProps, mergeStyle };
};
